import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import * as api from "../../../../api/generated-api";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../../api/axiosConfig";
import {
  getDealDocument,
  getDealDocumentContents,
  getDealDocumentSignatureSignaturePage,
} from "../../../../api/deals/documents";
import "react-pdf/dist/Page/TextLayer.css";
import { DealsPageLayout } from "../../dealsPageLayout/DealsPageLayout";
import DealDocumentPDF from "../ViewDocument";
import { getDealDocumentSignatures } from "../../../../api/deals/signatures";
import { DealSignaturePagePDF } from "../SignaturePagePDF";
import DocumentSignaturesSidebar from "./DocumentSignaturesSidebar";
import { clickToLinkHandler } from "../../../../helpers/utils";
import DealPageTitleAndAction from "../../common/DealPageTitleAndAction";

type ViewDocumentPageProps = {
  token: string;
  orgId: number;
  dealId: number;
  documentId: number;
};

const ViewDealDocumentWithSignaturesPage = ({
  orgId,
  dealId,
  documentId,
  token,
}: ViewDocumentPageProps) => {
  configureBaseUrl();
  configureAuthHeader(token);

  const [dealDocument, setDealDocument] = useState<api.DealDocument>();
  const [dealDocumentContents, setDealDocumentContents] = useState<Blob>();
  const [signedDocumentSignatures, setSignedDocumentSignatures] = useState<
    api.DealDocumentSignature[]
  >([]);
  const [signaturePagePdfs, setSignaturePagePdfs] = useState<
    Map<number, Buffer>
  >(new Map<number, Buffer>());

  useEffect(() => {
    getDealDocument(orgId, dealId, documentId, (fetchedDealDocument) => {
      setDealDocument(fetchedDealDocument);
    });

    getDealDocumentContents(orgId, dealId, documentId, (response) => {
      setDealDocumentContents(response.data);
    });

    getDealDocumentSignatures(orgId, dealId, (signatures) => {
      const signed = signatures.filter((signer) => {
        if (signer.dealDocumentId == documentId && signer.status == "signed") {
          return signer;
        }
      });

      setSignedDocumentSignatures(signed);

      const docSignerIdToPdfBuffer = new Map<number, Buffer>();

      Promise.all(
        signed.map(async (docSigner) => {
          const buffer = await getDealDocumentSignatureSignaturePage(
            orgId,
            dealId,
            documentId,
            docSigner.id
          );
          docSignerIdToPdfBuffer.set(docSigner.id, buffer);
        })
      ).then(() => {
        setSignaturePagePdfs(docSignerIdToPdfBuffer);
      });
    });
  }, [orgId, dealId, documentId]);

  if (!dealDocument || !dealDocumentContents) {
    return null;
  }

  const handleOnVersionsClick = () => {
    clickToLinkHandler(`/${orgId}/deals/${dealId}/documents/${documentId}`);
  };

  return (
    <DealsPageLayout
      showDealNav={false}
      leftHeaderAction={
        <DealPageTitleAndAction
          title={dealDocument.name}
          breadcrumbs={[
            {
              dataTestId: "back-to-all-documents",
              title: "Deal Documents",
              path: `/${orgId}/deals/${dealId}/documents`,
            },
          ]}
        />
      }
      sidebarTitle="Signatures"
      sidebar={
        <DocumentSignaturesSidebar onVersionsClick={handleOnVersionsClick} />
      }
      scrollable={false}
    >
      <DealDocumentPDF dealDocumentContents={dealDocumentContents}>
        {signedDocumentSignatures.map((signer, index) => {
          return (
            <DealSignaturePagePDF
              buffer={signaturePagePdfs.get(signer.id)}
              key={`signature-page-${index}`}
            />
          );
        })}
      </DealDocumentPDF>
    </DealsPageLayout>
  );
};

const mapStateToProps = (state, props): ViewDocumentPageProps => {
  const {
    match: {
      params: { dealId, orgId, documentId },
    },
  } = props;
  const { token } = state.auth;
  return {
    token,
    orgId,
    dealId,
    documentId,
  };
};

export default connect(mapStateToProps)(ViewDealDocumentWithSignaturesPage);
